import React, { useRef, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Div, ColumnDiv } from 'styles/elements/div';
import { H1 } from 'styles/elements/headers';
import { PrimaryButton, SecondaryButton } from 'styles/elements/buttons/primaryCare';
import media from 'styles/media';

const ESC_KEY = 27;

const ModalContent = styled(Div)`
  padding: 40px;
  background: ${props => props.theme.backgroundColorPrimary};
  flex-direction: column;
  z-index: 100;
  margin: 40px auto auto;
  align-items: stretch;
  align-self: flex-start;
  max-width: 400px;
  box-sizing: border-box;
  position: relative;
  ${media.IE11 ? `
    flex: 1 1 100%;
  ` : ''};
`;

const ModalOverlay = styled(Div)`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: black;
  transition: opacity 0.15s linear;
  opacity: 0.5;
  z-index: 99;
`;

const ModalContainer = styled(Div)`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  overflow: auto;
  z-index: 2;
`;

const Header = styled(H1)`
  margin-top: 0;
  font-size: 28px;
  margin-bottom: 35px;
  ${media.extraSmallAndBelow`
    font-size: 22px;
    margin-bottom: 15px;
    font-weight: normal;
    line-height: 1.2;
  `};
`;

const CancelButton = styled(SecondaryButton)`
  margin-top: 10px;
  font-size: 14px;
  ${media.extraSmallAndBelow`
    font-size: 12px;
    margin: 20px 0 0;
  `};
`;

const ConfirmButton = styled(PrimaryButton)`
  margin: 20px 0 0;
  padding: 25px 50px;
  font-size: 19px;
`;

const ChildContainer = styled(ColumnDiv)``;

export function Modal({ header, children, confirmButtonText, isOpen, onSelectConfirm, onSelectCancel, cancelButtonText }) {
  const confirmButtonRef = useRef();
  const cancelButtonRef = useRef();
  const [lastActiveElement, setLastActiveElement] = useState(null);
  const focusLastActiveElement = () => (lastActiveElement ? lastActiveElement.focus() : null);
  const handleOnSelectConfirm = () => {
    focusLastActiveElement();
    setLastActiveElement(null);
    onSelectConfirm();
  };
  const handleOnSelectCancel = () => {
    focusLastActiveElement();
    setLastActiveElement(null);
    onSelectCancel();
  };
  useEffect(() => {
    if (isOpen) {
      if (!lastActiveElement) {
        const { activeElement } = document;
        if (activeElement && (activeElement.href || activeElement.type)) {
          setLastActiveElement(activeElement);
        }
      }
      if (confirmButtonRef && confirmButtonRef.current) {
        confirmButtonRef.current.focus();
      } else if (cancelButtonRef && cancelButtonRef.current) {
        cancelButtonRef.current.focus();
      }
    } else if (lastActiveElement) {
      setLastActiveElement(null);
    }
  }, [isOpen, confirmButtonRef, cancelButtonRef, lastActiveElement]);
  const handleKeyDown = event => {
    if (event.keyCode === ESC_KEY) {
      event.stopPropagation();
      if (onSelectConfirm) {
        handleOnSelectConfirm(event);
      } else if (onSelectCancel) {
        handleOnSelectCancel(event);
      }
    }
  };
  return (isOpen && (
    <ModalContainer>
      <ModalOverlay />
      <ModalContent onKeyDown={handleKeyDown}>
        <Header>{header}</Header>
        <ChildContainer>
          {children}
        </ChildContainer>
        {confirmButtonText && <ConfirmButton ref={confirmButtonRef} onClick={handleOnSelectConfirm}>{confirmButtonText}</ConfirmButton>}
        {cancelButtonText && <CancelButton ref={cancelButtonRef} onClick={handleOnSelectCancel}>{cancelButtonText}</CancelButton>}
      </ModalContent>
    </ModalContainer>
  ));
}
